import { AutocompletePlugin, getAlgoliaResults } from "@algolia/autocomplete-js";
import type { Hit } from "@algolia/client-search";
import { SearchResponse } from "@algolia/client-search";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";
import { useTranslations } from "next-intl";

import IconChevronRight from "@/icons/icon-chevron-right";
import Icon from "~/components/common/icon";
import ProductPriceAlgolia from "~/components/products/product-price-algolia";
import ProductRatings from "~/components/products/product-ratings";
import { highlightToHtml } from "~/services/algolia-utils";
import Algolia from "~/types/algolia";
import CommonUtils from "~/utils/common-utils";
import imageUtils from "~/utils/image-utils";
import TrackingUtils from "~/utils/tracking-utils";

import stylesButton from "../../common/button.module.scss";
import { searchClient } from "../search-client";
import styles from "./../search-dialog.module.scss";

type imageAlgolia = {
  blob: string;
  name: string;
  type: string;
  id: string;
  url: string;
};

type priceAlgolia = {
  formattedDiscountPerc?: string;
  oldValue?: number;
  value: number;
};

type ProductRecord = {
  objectID: string;
  name: string;
  availability: string[];
  baseProduct: number;
  code: string;
  multicolor: string;
  color: string;
  description: string;
  averageRating: number;
  categories: { [key: string]: string };
  category: string;
  collection: boolean;
  coverage: string;
  finishEffect: string;
  stockStatus: boolean;
  images: imageAlgolia[] | imageAlgolia;
  baseBackendId: string;
  slug: string;
  reviewsNumber: number;
  siblings: number[];
  prices: priceAlgolia;
};

export type ProductHitType = Hit<ProductRecord>;

type ProductItemProps = {
  hit: ProductHitType;
  locale: string;
  searchQuery?: string;
  currency: string;
};

export function ProductHit({ hit, locale, searchQuery, currency }: ProductItemProps) {
  const t = useTranslations();

  return (
    <Link
      href={`/${locale}/p/${hit.slug}-${hit.objectID}`}
      className={styles.searchProduct}
      onClick={() => {
        if ("__autocomplete_queryID" in hit) {
          // save it in localstorage for cross-tabs navigation and set it as default on session
          localStorage.setItem("algolia_queryid", hit.__autocomplete_queryID as string);
          sessionStorage.setItem("algolia_queryid", hit.__autocomplete_queryID as string);
        }
        TrackingUtils.track(
          TrackingUtils.mapEcommerceEvent("select_item", [
            TrackingUtils.mapAlgoliaProduct(
              { ...hit, currency },
              {
                //index: product.__position, -> Product position is not returned by Algolia
                item_list_name: "algolia_search",
              }
            ),
          ])
        );

        TrackingUtils.trackAlgoliaProductClick(
          hit.name,
          searchQuery,
          !searchQuery ? t("generic.noResultsTitleOverlay") : t("generic.products")
        );
      }}
    >
      <Image
        src={
          (Array.isArray(hit.images)
            ? hit.images?.find((img: { name: string }) => img.name == "primary")?.url
            : hit.images?.url) || imageUtils.transparentPixel
        }
        alt={hit.name}
        width={80}
        height={80}
        className={styles.searchImg}
      />

      <div className={styles.searchProductCol}>
        {/* <ProductListingTags product={product} /> */}
        <h3>
          {hit._highlightResult?.name ? (
            <p
              className={classNames(styles.upperFirstLetter, styles.clamp)}
              dangerouslySetInnerHTML={{
                __html: highlightToHtml(hit._highlightResult.name.value),
              }}
            />
          ) : (
            <p className={classNames(styles.upperFirstLetter, styles.clamp)}>{hit.name}</p>
          )}
        </h3>
        <div className={styles.searchProductRow}>
          <ProductPriceAlgolia price={hit.prices} />
          <div className={styles.ratings}>
            <ProductRatings average={hit.averageRating || 0} numberOfRatings={hit.reviewsNumber} />
          </div>
        </div>
        {hit.siblings?.length > 0 && (
          <div className={styles.searchShadesAvailables}>
            <span>{t("generic.shadesavailables", { count: hit.siblings.length })}</span>
            {/* <Icon icon={IconChevronRight} width={16} height={16} aria-hidden /> */}
          </div>
        )}
      </div>
    </Link>
  );
}

export const productsPlugin = (
  algoliaIndexId: string,
  width: number,
  viewAll: string,
  locale: string,
  newRecent: (newItem: string) => void,
  currency: string
) => {
  const algoliaSearchConfig = CommonUtils.parseJsonSafely(
    process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_CONFIG!
  ) as Algolia.Configuration;
  const distinct = algoliaSearchConfig?.distinct;

  return {
    getSources({ query }) {
      if (!query) {
        return [];
      }

      return [
        {
          sourceId: "productsPlugin",
          getItems({ setContext }) {
            return getAlgoliaResults<ProductHitType>({
              searchClient,
              queries: [
                {
                  indexName: algoliaIndexId,
                  query,
                  params: {
                    hitsPerPage: width >= 1280 ? 8 : width >= 900 ? 6 : 4,
                    distinct,
                  },
                },
              ],
              transformResponse({ hits, results }) {
                setContext({
                  nbProducts: (results[0] as SearchResponse<ProductHitType>).nbHits,
                  renderingContent: (results[0] as SearchResponse<ProductHitType>).renderingContent,
                });

                return hits;
              },
            });
          },
          onSelect({ setIsOpen }) {
            setIsOpen(true);
          },
          templates: {
            item({ item }) {
              return <ProductHit hit={item} locale={locale} searchQuery={query} currency={currency} />;
            },
            footer({ state }) {
              return (
                (state.context.nbProducts as number) > 4 && (
                  <Link
                    href={`/${locale}/search?${algoliaIndexId}${encodeURIComponent("[query]")}=${encodeURIComponent(
                      state.query
                    )}`}
                    onClick={() => {
                      newRecent(state.query);
                      TrackingUtils.trackAlgoliaProductClick(viewAll, state.query, "CTA");
                    }}
                    className={classNames(stylesButton.button, stylesButton.primary, styles.searchButton)}
                  >
                    {viewAll}
                  </Link>
                )
              );
            },
          },
        },
      ];
    },
  } as AutocompletePlugin<ProductHitType, {}>;
};
